import { useEffect, useMemo, useState } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import { preprocessWikiLinks, isInternalDocLink, decodeDocHref } from "../../lib/docs/wiki-links";
import {
  checkboxIdFromText,
  subscribeToDocChecks,
  toggleChecked,
} from "../../lib/docs/checkbox-state";

interface Props {
  content: string;
  docPath: string;
  onNavigate: (docPath: string) => void;
  onCheckboxChange?: () => void;
}

export default function MarkdownRenderer({ content, docPath, onNavigate, onCheckboxChange }: Props) {
  const processed = useMemo(() => preprocessWikiLinks(content), [content]);

  return (
    <div className="markdown-body max-w-none text-sm leading-6 text-slate-800 dark:text-slate-200">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
        urlTransform={(url) => {
          // Preserve our internal docs:// scheme so onClick can intercept.
          // react-markdown's default only allows http/https/mailto/tel/ftp/file
          // and would otherwise strip the href, breaking wiki-link navigation.
          if (url.startsWith("docs://")) return url;
          return defaultUrlTransform(url);
        }}
        components={{
          a({ href, children, ...rest }) {
            if (isInternalDocLink(href)) {
              const target = decodeDocHref(href!);
              return (
                <a
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate(target);
                  }}
                  className="text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                >
                  {children}
                </a>
              );
            }
            return (
              <a
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                className="text-indigo-600 dark:text-indigo-400 hover:underline"
                {...rest}
              >
                {children}
              </a>
            );
          },
          // GFM task-list items — replace the default disabled checkbox with
          // an interactive one persisted to localStorage.
          li({ className, children, ...rest }) {
            const isTask = typeof className === "string" && className.includes("task-list-item");
            if (!isTask) {
              return <li {...rest}>{children}</li>;
            }
            return <TaskListItem docPath={docPath} onCheckboxChange={onCheckboxChange}>{children}</TaskListItem>;
          },
          // Tighter defaults for common blocks
          h1: ({ children }) => <h1 className="text-2xl font-semibold mt-4 mb-3">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-semibold mt-6 mb-2 border-b border-slate-200 dark:border-slate-800 pb-1">{children}</h2>,
          h3: ({ children }) => <h3 className="text-base font-semibold mt-4 mb-2">{children}</h3>,
          p: ({ children }) => <p className="my-2">{children}</p>,
          ul: ({ className, children }) => <ul className={`list-disc pl-5 my-2 space-y-1 ${className ?? ""}`}>{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>,
          code: ({ children, className }) => (
            <code className={`px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[0.85em] ${className ?? ""}`}>{children}</code>
          ),
          pre: ({ children }) => (
            <pre className="bg-slate-900 text-slate-100 p-3 rounded-lg overflow-x-auto text-xs my-3">{children}</pre>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-3">
              <table className="min-w-full text-sm border-collapse">{children}</table>
            </div>
          ),
          th: ({ children }) => <th className="text-left px-2 py-1 border-b border-slate-300 dark:border-slate-700 font-semibold">{children}</th>,
          td: ({ children }) => <td className="px-2 py-1 border-b border-slate-100 dark:border-slate-800 align-top">{children}</td>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-indigo-400 pl-3 my-2 text-slate-600 dark:text-slate-300 italic">{children}</blockquote>
          ),
          hr: () => <hr className="my-6 border-slate-200 dark:border-slate-800" />,
        }}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
}

/** Task-list item with an interactive checkbox keyed to the task text. */
function TaskListItem({
  docPath,
  children,
  onCheckboxChange,
}: {
  docPath: string;
  children: React.ReactNode;
  onCheckboxChange?: () => void;
}) {
  // remark-gfm renders the item as [Input (disabled checkbox), ...rest]
  // Extract the text from `rest` to key on; skip the input on render.
  const text = extractText(children).trim();
  const id = checkboxIdFromText(text || "task");

  const [checked, setLocalChecked] = useState<boolean>(false);

  useEffect(() => {
    // Subscribe to the doc's shared checked-set; flip our local state
    // whenever the module cache is refreshed (initial fetch or a toggle
    // originating anywhere in the app for this doc).
    const unsub = subscribeToDocChecks(docPath, (set) => {
      setLocalChecked(set.has(id));
    });
    return unsub;
  }, [docPath, id]);

  async function handleToggle(next: boolean) {
    // Optimistic: flip immediately, revert if the PUT rejects. The
    // subscription will re-sync from the cache after a successful toggle
    // (that emit is a no-op visually since we already show the new state).
    setLocalChecked(next);
    onCheckboxChange?.();
    try {
      await toggleChecked(docPath, id, next);
    } catch (err) {
      console.warn("doc-checks write failed", err);
      setLocalChecked(!next);
      onCheckboxChange?.();
    }
  }

  return (
    <li className="list-none -ml-5 flex items-start gap-2 my-0.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          void handleToggle(e.target.checked);
        }}
        className="mt-1 accent-indigo-600 cursor-pointer"
      />
      <span className={checked ? "line-through text-slate-400" : ""}>
        {stripLeadingCheckbox(children)}
      </span>
    </li>
  );
}

function extractText(node: React.ReactNode): string {
  if (node == null || node === false) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (typeof node === "object" && "props" in (node as any)) {
    return extractText((node as any).props?.children);
  }
  return "";
}

function stripLeadingCheckbox(children: React.ReactNode): React.ReactNode {
  // Task-list items ship as [inputCheckbox, ...text]. Drop the input; keep the rest.
  if (Array.isArray(children)) {
    return children.filter((c: any) => {
      if (c == null || typeof c === "string") return true;
      if (typeof c === "object" && "type" in c) {
        return (c as any).type !== "input";
      }
      return true;
    });
  }
  return children;
}
